/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';

import { VideoIcon } from '@kaiizen/kai-shiki/react';

import {
  DEFAULT_VIDEO_LIST_THUMBNAIL_STYLES,
  type VideoListThumbnailStyles,
} from './videoListThumbnailStyles';

const FIRST_FRAME_SECONDS = 0.01;

type VideoListThumbnailProps = {
  playbackUrl?: string | null;
  label: string;
  styles?: VideoListThumbnailStyles;
};

/**
 * Shows the first decoded frame using a paused `<video preload="metadata">`.
 * Loads only when the row nears the viewport so a long list does not fan out
 * metadata requests on mount.
 */
export const VideoListThumbnail = ({
  playbackUrl,
  label,
  styles = DEFAULT_VIDEO_LIST_THUMBNAIL_STYLES,
}: VideoListThumbnailProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    setHasLoadError(false);
    setShouldLoad(false);
  }, [playbackUrl]);

  useEffect(() => {
    if (!playbackUrl || hasLoadError) return;

    const node = containerRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [playbackUrl, hasLoadError]);

  if (!playbackUrl || hasLoadError) {
    return (
      <div css={[styles.container, styles.fallback]} aria-hidden={'true'}>
        <VideoIcon size={19} color={'currentColor'} />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      css={styles.container}
      aria-hidden={'true'}
    >
      {shouldLoad
        ? (
          <video
            css={styles.video}
            preload={'metadata'}
            muted
            playsInline
            src={playbackUrl}
            aria-label={label}
            onLoadedMetadata={event => {
              event.currentTarget.currentTime = FIRST_FRAME_SECONDS;
            }}
            onSeeked={event => {
              event.currentTarget.pause();
            }}
            onPlay={event => {
              event.currentTarget.pause();
            }}
            onError={() => setHasLoadError(true)}
          />
        )
        : (
          <div css={styles.fallback}>
            <VideoIcon size={19} color={'currentColor'} />
          </div>
        )}
    </div>
  );
};
